import { Button, Modal, Stack, Typography } from '@mui/material'
import ArrowLeftNewSvg from '~/@ui/_sharedSvgs/ui/ArrowLeftNew.svg?react'
import { IInvestmentProduct } from '~/@api/generated-api-types/global.types'
import SwipeToConfirm from './SwipeToConfirm'
import { useState } from 'react'
import { InfoBubble } from '~/@ui/info-bubble/InfoBubble'

type Props = {
  isOpen: boolean
  handleClose: () => void
  opportunity: IInvestmentProduct | null
  availableFunds: number
  orderDetails: {
    hasOneTimeInvestment: boolean
    oneTimeInvestmentAmount: number
    hasMonthlyInvestment: true
    monthlyInvestmentAmount: number
    investmentProductId: string
    riskAcknowledged: boolean
    durationInMonths: number
  }
  createInvestment: () => Promise<void>
}

export const RecapModal = ({
  isOpen,
  handleClose,
  opportunity,
  availableFunds,
  orderDetails,
  createInvestment,
}: Props) => {
  const [swipeResetTrigger, setSwipeResetTrigger] = useState(0)

  const oneTimeInvestmentAmount = orderDetails.hasMonthlyInvestment
    ? orderDetails.oneTimeInvestmentAmount
    : 0
  const monthlyInvestmentAmount = orderDetails.monthlyInvestmentAmount
  const requiredAmount = oneTimeInvestmentAmount + monthlyInvestmentAmount
  const hasEnoughFunds = availableFunds >= requiredAmount

  const handleCreateInvestment = async () => {
    try {
      await createInvestment()
    } catch (error) {
      setSwipeResetTrigger((prev) => prev + 1)
    }
  }

  return (
    <Modal open={isOpen} onClose={handleClose} className="overflow-auto">
      <Stack className="w-full min-h-screen pt-4 pb-28 bg-zWhite100 ">
        <Stack className="w-full max-w-[768px] self-center">
          <Button className="w-16 mx-6 bg-zWhite200" onClick={handleClose}>
            <ArrowLeftNewSvg />
          </Button>

          <Stack className="px-5 mt-10">
            <Typography className="text-2xl font-bold text-zBlack200">
              Rekapitulácia objednávky
            </Typography>
            <Typography className="text-3d font-medium text-zBlack200">
              {`ID produktu: ${opportunity?.id.replace(/[^0-9]/g, '')}`}
            </Typography>
          </Stack>

          <Stack className="gap-3 px-6 mt-[30px]">
            <Typography className="text-base font-bold text-zBlack200">
              Prehľad investície
            </Typography>

            <Stack className="p-4 pr-3 bg-zGrey200 rounded-4d">
              <Stack className="gap-2.5 pb-[18px] border-b border-zBlack200 border-dashed">
                <Stack className="flex-row justify-between">
                  <Typography className="text-3d font-medium text-zBlack200 mr-auto">
                    Jednorázová investícia
                  </Typography>
                  <Typography className="text-3d font-medium text-zBlack200 mr-2.5">
                    {`${oneTimeInvestmentAmount.toLocaleString('sk-SK')} €`}
                  </Typography>
                  <InfoBubble text="Jednorazová suma, ktorú vložíte ako prvý vklad a ktorá pozitívne ovplyvní váš výnos." />
                </Stack>
                <Stack className="flex-row justify-between">
                  <Typography className="text-3d font-medium text-zBlack200 mr-auto">
                    Mesačná investícia
                  </Typography>
                  <Typography className="text-3d font-medium text-zBlack200 mr-2.5">
                    {`${monthlyInvestmentAmount.toLocaleString('sk-SK')} €`}
                  </Typography>
                  <InfoBubble text="Suma, ktorú budete každý mesiac (k 1. dňu v mesiaci) posielať do Mojej peňaženky pre stabilný budúci príjem." />
                </Stack>
              </Stack>

              <Stack className="flex-row items-center pt-5">
                <Typography className="text-3d font-medium text-zBlack200 mr-auto">
                  Na úhradu
                </Typography>
                <Typography className="text-[22px] font-bold text-zBlack200 mr-2.5">
                  {`${requiredAmount.toLocaleString('sk-SK')} €`}
                </Typography>
                <InfoBubble text="Celková suma jednorazovej a mesačnej investície, ktorú je potrebné uhradiť pre objednávku investície." />
              </Stack>
            </Stack>

            <Typography className="text-base font-bold text-zBlack200 mt-5">
              Spôsob platby
            </Typography>

            <Stack
              className={`px-4 py-3.5 pr-3 mt-1 ${hasEnoughFunds ? 'bg-zGrey200' : 'bg-[#FFE4DA]'} rounded-4d`}>
              <Stack className="flex-row items-center">
                <Typography className="text-3d font-medium text-zBlack200 mr-auto">
                  Moja peňaženka
                </Typography>
                <Typography className="text-[22px] font-bold text-zBlack200 mr-2.5">
                  {`${availableFunds.toLocaleString('sk-SK')} €`}
                </Typography>
              </Stack>
            </Stack>
            <Typography className="text-3d font-medium text-zGrey400 mt-5">
              {hasEnoughFunds
                ? 'Gratulujeme! Máte dostatok prostriedkov vo vašej peňaženke na rezervovanie tejto investície. Po potvrdení rezervácie vás začneme párovať so seniorom. Tento proces môže trvať niekoľko dní.'
                : 'Ospravedlňujeme sa, momentálne nemáte dostatok prostriedkov. Nemusíte sa obávať – po rezervácii investície vás v ďalšom kroku vyzveme k doplneniu peňaženky. Po pripísaní sumy investíciu automaticky potvrdíme a začneme vás párovať so seniorom. Tento proces môže trvať niekoľko dní.'}
            </Typography>

            <SwipeToConfirm
              lightTheme
              label={
                hasEnoughFunds ? 'Zaplatiť investíciu' : 'Rezervovať investíciu'
              }
              className="mt-7"
              onConfirm={handleCreateInvestment}
              resetTrigger={swipeResetTrigger}
            />

            <Typography className="text-xs font-semibold text-zGrey400 text-center mt-3">
              {hasEnoughFunds
                ? 'Pre zaplatenie investície potiahnite prstom doprava'
                : 'Pre rezerváciu investície potiahnite prstom doprava'}
            </Typography>
          </Stack>
        </Stack>
      </Stack>
    </Modal>
  )
}
